import { useLocation, useNavigate } from "@tanstack/react-router";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCurrentOrg } from "@/hooks/use-current-org";
import { useAuthState } from "@/lib/auth-state";
import { markViewAsExit, useViewAsVisitor, VIEW_AS_PARAM } from "@/lib/view-as";

/**
 * Banner for the signed-in visitor preview (`?view_as=visitor`). Shows only
 * when the param actually downgrades something (a member previewing), with a
 * one-click exit that strips the param. True visitors never see it.
 */
export function ViewAsVisitorBar() {
	const { session } = useAuthState();
	const { org } = useCurrentOrg();
	const viewAsVisitor = useViewAsVisitor();
	const location = useLocation();
	const navigate = useNavigate();

	if (!viewAsVisitor || !session || !org?.is_member) return null;

	const handleExit = () => {
		const rest = { ...(location.search as Record<string, unknown>) };
		delete rest[VIEW_AS_PARAM];
		markViewAsExit();
		void navigate({
			to: location.pathname as "/",
			search: rest as never,
			replace: true,
		});
	};

	return (
		<div
			role="status"
			className="z-30 border-b border-border bg-amber-500/10"
		>
			<div className="mx-auto flex max-w-screen-2xl items-center gap-3 px-4 py-2 lg:px-6">
				<Eye className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
				<p className="flex-1 text-sm">
					Previewing <span className="font-medium">{org.name}</span> as a
					visitor. Other members see the full workspace.
				</p>
				<Button size="sm" variant="outline" onClick={handleExit}>
					Exit preview
				</Button>
			</div>
		</div>
	);
}
